import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import type { IssueDetail } from "@klantpunt/shared";
import { putIssue } from "../features/issues/useIssues";
import { useMe } from "../lib/auth";
import { restoreLocalPhotos } from "../lib/photos";
import { useToast } from "../lib/toast";
import { queue } from "./queue";

/** In de app-shell: wachtrij koppelen aan de ingelogde gebruiker en na synchroniseren verversen. */
export function useOfflineSync() {
  const { data: me } = useMe();
  const qc = useQueryClient();
  const toast = useToast();
  const userId = me?.user.id;
  const orgId = me?.org?.id;

  useEffect(() => {
    queue.setOwner(userId && orgId ? { userId, orgId } : null);
  }, [userId, orgId]);

  useEffect(() => {
    void restoreLocalPhotos();
    const offDrained = queue.onDrained(() => {
      for (const key of [
        "issues",
        "issue",
        "tasks",
        "my-tasks",
        "obligations",
        "obligation-list",
        "building",
        "condition-assessment",
        "meter-round",
      ])
        void qc.invalidateQueries({ queryKey: [key] });
    });
    const offFailed = queue.onFailed((op, message) => toast(`${op.label} niet verwerkt: ${message}`));
    // Antwoord van de server (bijgewerkte bevinding) meteen in de cache, ook voor latere wijzigingen.
    const offDone = queue.onDone((op, data) => {
      const d = data as Partial<IssueDetail> | undefined;
      if (op.buildingId && d && typeof d.id === "string" && Array.isArray(d.events))
        putIssue(qc, op.buildingId, d as IssueDetail);
    });
    return () => {
      offDone();
      offDrained();
      offFailed();
    };
  }, [qc, toast]);
}

/**
 * Gebouw waar je mee bezig bent: alle tekeningen alvast ophalen, zodat de service worker ze bewaart
 * en ze ook in de kelder zonder bereik te openen zijn (SPEC §11). Eén voor één, na het laden.
 */
export function usePrefetchPlans(fileIds: string[]) {
  const key = fileIds.join(",");
  useEffect(() => {
    if (!key || !navigator.onLine || !navigator.serviceWorker?.controller) return;
    let stop = false;
    const timer = setTimeout(async () => {
      for (const id of key.split(",")) {
        if (stop) return;
        try {
          await fetch(`/api/files/${id}/url`, { credentials: "same-origin" }).then((r) => r.arrayBuffer());
        } catch {
          return;
        }
      }
    }, 1500);
    return () => {
      stop = true;
      clearTimeout(timer);
    };
  }, [key]);
}
